import { useState } from "react";
import { Button, Input, Popover, PopoverSurface, PopoverTrigger, Text, makeStyles, tokens } from "@fluentui/react-components";
import { Checkmark16Regular, Copy16Regular, Send16Regular } from "@fluentui/react-icons";
import { normalisePhone } from "@gymplan/shared";
import { emit } from "../lib/analytics.ts";

const useStyles = makeStyles({
  invite: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px", padding: "6px 6px 6px 12px", borderRadius: tokens.borderRadiusMedium, backgroundColor: tokens.colorNeutralBackground3, maxWidth: "100%", minWidth: 0 },
  url: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, flex: "1 1 auto", color: tokens.colorNeutralForeground2, "@media (max-width: 520px)": { display: "none" } },
  label: { whiteSpace: "nowrap", "@media (max-width: 520px)": { flexBasis: "100%" } },
  btn: { whiteSpace: "nowrap", flexShrink: 0 },
  surface: { display: "flex", flexDirection: "column", gap: "10px", width: "320px", maxWidth: "calc(100vw - 32px)" },
  row: { display: "flex", gap: "8px" },
  hint: { color: tokens.colorNeutralForeground3 },
  error: { color: tokens.colorPaletteRedForeground1 },
});

/** The message the client receives, from the trainer's own WhatsApp. */
export const inviteMessage = (trainerFirst: string, url: string) =>
  `Hi, it's ${trainerFirst}. I use RackUp to plan our training sessions. Tap this link to tell me when you can train — it takes about a minute: ${url}`;

/**
 * The client sign-up link, with Copy and "Send on WhatsApp". Sending opens the trainer's own WhatsApp
 * with the message ready (wa.me), so it comes from a number the client knows. The number isn't stored.
 */
export function InviteSend({ url, trainerFirst }: { url: string; trainerFirst: string }) {
  const s = useStyles();
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const [number, setNumber] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const text = encodeURIComponent(inviteMessage(trainerFirst, url));

  const copy = () =>
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      emit({ event: "invite_shared", properties: { via: "copy" } });
      setTimeout(() => setCopied(false), 2000);
    });

  const openWa = (to: string | null) => {
    window.open(`https://wa.me/${to ? to.replace(/^\+/, "") : ""}?text=${text}`, "_blank", "noopener,noreferrer");
    emit({ event: "invite_shared", properties: { via: to ? "whatsapp_number" : "whatsapp_contact" } });
    setOpen(false);
    setNumber("");
  };

  const send = () => {
    const to = normalisePhone(number);
    if (!to) return setErr("That doesn't look like a mobile number — try 07700 900123 or +44 7700 900123.");
    setErr(null);
    openWa(to);
  };

  return (
    <div className={s.invite} title="Clients add themselves from this link. Rotate it in Settings.">
      <Text size={200} weight="semibold" className={s.label}>
        Client sign-up link
      </Text>
      <Text size={200} className={s.url}>
        {url.replace(/^https?:\/\//, "")}
      </Text>
      <Button size="small" className={s.btn} icon={copied ? <Checkmark16Regular /> : <Copy16Regular />} onClick={copy}>
        {copied ? "Copied" : "Copy"}
      </Button>
      <Popover open={open} onOpenChange={(_, d) => { setOpen(d.open); setErr(null); }} positioning="below-end" trapFocus>
        <PopoverTrigger disableButtonEnhancement>
          <Button size="small" appearance="primary" className={s.btn} icon={<Send16Regular />}>
            Send on WhatsApp
          </Button>
        </PopoverTrigger>
        <PopoverSurface className={s.surface} aria-label="Send the sign-up link on WhatsApp">
          <Text weight="semibold">Send the link on WhatsApp</Text>
          <form className={s.row} onSubmit={(e) => { e.preventDefault(); send(); }}>
            <Input style={{ flex: 1 }} type="tel" inputMode="tel" autoComplete="off" placeholder="Client's number, e.g. 07700 900123" value={number} onChange={(_, d) => setNumber(d.value)} aria-label="Client's WhatsApp number" maxLength={20} autoFocus />
            <Button type="submit" appearance="primary" disabled={!number.trim()}>
              Send link
            </Button>
          </form>
          {err && <Text size={200} className={s.error} role="alert">{err}</Text>}
          <Button size="small" appearance="subtle" onClick={() => openWa(null)} style={{ alignSelf: "flex-start" }}>
            Or pick someone from your WhatsApp contacts
          </Button>
          <Text size={200} className={s.hint}>
            Opens WhatsApp with the message ready, so it comes from you. RackUp doesn't keep the number — they add their own details when they sign up.
          </Text>
        </PopoverSurface>
      </Popover>
    </div>
  );
}
